import { Flex, ReportSection } from '@/components';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import type { LedgerKsFundAdditionalIncomeAndExpenditureInfoSectionProps } from '@/features/mold/models/ledger';
import { FundAdditionalIncomeAndExpenditureModel } from '@/features/project/projects/_models/fund-additiona-income-and-expenditure';
import {
  calcAdministrativeExpensesRate,
  getClericalExpenditureSummaryByType,
  getExpenditureSummaryByType,
  getIncomeAndExpenditureSummaryByType,
  getIncomeSummaryByType,
  getRecentFundIncomeAndExpenditureList,
} from '@/features/project/projects/detail/income-and-expenditure/_utils/fund-income-and-expenditure-table';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type DivergenceInExecutionItemBefore2025,
  KsFundDivergenceInExecutionItemBefore2025,
} from './KsFundDivergenceInExecutionItemBefore2025';
import {
  type IncomeAndExpenditureItem,
  KsFundIncomeAndExpenditureItem,
  emptyIncomeAndExpenditureItem,
} from './KsFundIncomeAndExpenditureItem';

export const KsFundAdditionalIncomeAndExpenditureInfoSectionBefore2025: FC<
  LedgerKsFundAdditionalIncomeAndExpenditureInfoSectionProps
> = ({ project }) => {
  const { t } = useTranslation();
  const { devtoolState } = useDevToolsStorage();
  const isDebugMode = !!devtoolState?.isDebugMode;

  const parsed = FundAdditionalIncomeAndExpenditureModel.parse(
    project?.fundAdditionalIncomeAndExpenditure,
  );

  // 帳票プレビューでは過去年度を最大3年分に制限（年度降順ソート）
  const pastFundAdditionalIncomeAndExpenditureList =
    getRecentFundIncomeAndExpenditureList({
      value: parsed,
      fiscalYear: project?.fiscalYear ?? 0,
      maxYears: 3,
    });

  const executionDivergences =
    project?.fundAdditionalAttributes?.executionDivergences;

  const { oneYearAgoDivergence, twoYearsAgoDivergence } = parsed;

  const IncomeAndExpenditureItems: IncomeAndExpenditureItem[] = [
    ...[...pastFundAdditionalIncomeAndExpenditureList].reverse().map((d) => ({
      title: d.fiscalYear?.toString() ?? '',
      beginningOfTheFundBalance: d.beginningOfTheFundBalance,
      estimatedGrantedAmountByTheCountry: d.grantedAmountByTheCountry,
      estimatedInvestmentIncome: d.investmentIncome,
      estimatedNationalEquivalentOfInvestmentIncome:
        d.nationalEquivalentOfInvestmentIncome,
      estimatedBusinessIncome: d.businessIncome,
      estimatedNationalEquivalentOfBusinessIncome:
        d.nationalEquivalentOfBusinessIncome,
      estimatedOtherIncomeAmount: d.otherIncomeAmount,
      totalIncome: getIncomeSummaryByType({
        fundAdditionalIncomeAndExpenditure: d,
        type: 'previous-year-performance',
        isDebugMode,
      }).value,
      estimatedBusinessExpense: d.businessExpense,
      estimatedAdministrativeExpense: d.administrativeExpense,
      estimatedAdministrativeExpenseRate: calcAdministrativeExpensesRate({
        administrativeExpense: d.administrativeExpense,
        sum: getExpenditureSummaryByType({
          fundAdditionalIncomeAndExpenditure: d,
          type: 'previous-year-performance',
          isDebugMode,
        }).value,
      }),
      estimatedChoreExpensesOfFundEstablishing:
        d.choreExpensesOfFundEstablishing,
      estimatedPersonnelExpensesOfFundEstablishing:
        d.personnelExpensesOfFundEstablishing,
      totalExpenditure: getExpenditureSummaryByType({
        fundAdditionalIncomeAndExpenditure: d,
        type: 'previous-year-performance',
        isDebugMode,
      }).value,
      estimatedAmountReturnedToTheNationalTreasury:
        d.amountReturnedToTheNationalTreasury,
      estimatedAmountReturnedToTheOrganization:
        d.amountReturnedToTheOrganization,
      fundBalanceAtTheEndOfYear: getIncomeAndExpenditureSummaryByType({
        fundAdditionalIncomeAndExpenditure: d,
        type: 'previous-year-performance',
        isDebugMode,
      }).value,
      estimatedNationalEquivalentOfTheBalance: d.nationalEquivalentOfTheBalance,
      estimatedChoreExpensesNotFromThisFund: d.choreExpensesNotFromThisFund,
      estimatedPersonnelExpensesNotFromThisFund:
        d.personnelExpensesNotFromThisFund,
      estimatedTotalAmountClericalExpenses: getClericalExpenditureSummaryByType(
        {
          fundAdditionalIncomeAndExpenditure: d,
          type: 'previous-year-performance',
          isDebugMode,
        },
      ).value,
    })),
    {
      title: t('current-fiscal-year-estimation'),
      beginningOfTheFundBalance: parsed.beginningOfTheFundBalance,
      estimatedGrantedAmountByTheCountry:
        parsed.estimatedGrantedAmountByTheCountry,
      estimatedInvestmentIncome: parsed.estimatedInvestmentIncome,
      estimatedNationalEquivalentOfInvestmentIncome:
        parsed.estimatedNationalEquivalentOfInvestmentIncome,
      estimatedBusinessIncome: parsed.estimatedBusinessIncome,
      estimatedNationalEquivalentOfBusinessIncome:
        parsed.estimatedNationalEquivalentOfBusinessIncome,
      estimatedOtherIncomeAmount: parsed.estimatedOtherIncomeAmount,
      totalIncome: getIncomeSummaryByType({
        fundAdditionalIncomeAndExpenditure: parsed,
        type: 'estimated-this-year',
        isDebugMode,
      }).value,
      estimatedBusinessExpense: parsed.estimatedBusinessExpense,
      estimatedAdministrativeExpense: parsed.estimatedAdministrativeExpense,
      estimatedAdministrativeExpenseRate: calcAdministrativeExpensesRate({
        administrativeExpense: parsed.estimatedAdministrativeExpense,
        sum: getExpenditureSummaryByType({
          fundAdditionalIncomeAndExpenditure: parsed,
          type: 'estimated-this-year',
          isDebugMode,
        }).value,
      }),
      estimatedChoreExpensesOfFundEstablishing:
        parsed.estimatedChoreExpensesOfFundEstablishing,
      estimatedPersonnelExpensesOfFundEstablishing:
        parsed.estimatedPersonnelExpensesOfFundEstablishing,
      totalExpenditure: getExpenditureSummaryByType({
        fundAdditionalIncomeAndExpenditure: parsed,
        type: 'estimated-this-year',
        isDebugMode,
      }).value,
      estimatedAmountReturnedToTheNationalTreasury:
        parsed.estimatedAmountReturnedToTheNationalTreasury,
      estimatedAmountReturnedToTheOrganization:
        parsed.estimatedAmountReturnedToTheOrganization,
      fundBalanceAtTheEndOfYear: getIncomeAndExpenditureSummaryByType({
        fundAdditionalIncomeAndExpenditure: parsed,
        type: 'estimated-this-year',
        isDebugMode,
      }).value,
      estimatedNationalEquivalentOfTheBalance:
        parsed.estimatedNationalEquivalentOfTheBalance,
      estimatedChoreExpensesNotFromThisFund:
        parsed.estimatedChoreExpensesNotFromThisFund,
      estimatedPersonnelExpensesNotFromThisFund:
        parsed.estimatedPersonnelExpensesNotFromThisFund,
      estimatedTotalAmountClericalExpenses: getClericalExpenditureSummaryByType(
        {
          fundAdditionalIncomeAndExpenditure: parsed,
          type: 'estimated-this-year',
          isDebugMode,
        },
      ).value,
    },
    {
      title: t('next-fiscal-year-estimation'),
      beginningOfTheFundBalance:
        parsed.beginningOfTheEstimatedFundBalanceNextYear,
      estimatedGrantedAmountByTheCountry:
        parsed.estimatedGrantedAmountByTheCountryNextYear,
      estimatedInvestmentIncome: parsed.estimatedInvestmentIncomeNextYear,
      estimatedNationalEquivalentOfInvestmentIncome:
        parsed.estimatedNationalEquivalentOfInvestmentIncomeNextYear,
      estimatedBusinessIncome: parsed.estimatedBusinessIncomeNextYear,
      estimatedNationalEquivalentOfBusinessIncome:
        parsed.estimatedNationalEquivalentOfBusinessIncomeNextYear,
      estimatedOtherIncomeAmount: parsed.estimatedOtherIncomeAmountNextYear,
      totalIncome: getIncomeSummaryByType({
        fundAdditionalIncomeAndExpenditure: parsed,
        type: 'estimated-next-year',
        isDebugMode,
      }).value,
      estimatedBusinessExpense: parsed.estimatedBusinessExpenseNextYear,
      estimatedAdministrativeExpense:
        parsed.estimatedAdministrativeExpenseNextYear,
      estimatedAdministrativeExpenseRate: calcAdministrativeExpensesRate({
        administrativeExpense: parsed.estimatedAdministrativeExpenseNextYear,
        sum: getExpenditureSummaryByType({
          fundAdditionalIncomeAndExpenditure: parsed,
          type: 'estimated-next-year',
          isDebugMode,
        }).value,
      }),
      estimatedChoreExpensesOfFundEstablishing:
        parsed.estimatedChoreExpensesOfFundEstablishingNextYear,
      estimatedPersonnelExpensesOfFundEstablishing:
        parsed.estimatedPersonnelExpensesOfFundEstablishingNextYear,
      totalExpenditure: getExpenditureSummaryByType({
        fundAdditionalIncomeAndExpenditure: parsed,
        type: 'estimated-next-year',
        isDebugMode,
      }).value,
      estimatedAmountReturnedToTheNationalTreasury:
        parsed.estimatedAmountReturnedToTheNationalTreasuryNextYear,
      estimatedAmountReturnedToTheOrganization:
        parsed.estimatedAmountReturnedToTheOrganizationNextYear,
      fundBalanceAtTheEndOfYear: getIncomeAndExpenditureSummaryByType({
        fundAdditionalIncomeAndExpenditure: parsed,
        type: 'estimated-next-year',
        isDebugMode,
      }).value,
      estimatedNationalEquivalentOfTheBalance:
        parsed.estimatedNationalEquivalentOfTheBalanceNextYear,
      estimatedChoreExpensesNotFromThisFund:
        parsed.estimatedChoreExpensesNotFromThisFundNextYear,
      estimatedPersonnelExpensesNotFromThisFund:
        parsed.estimatedPersonnelExpensesNotFromThisFundNextYear,
      estimatedTotalAmountClericalExpenses: getClericalExpenditureSummaryByType(
        {
          fundAdditionalIncomeAndExpenditure: parsed,
          type: 'estimated-next-year',
          isDebugMode,
        },
      ).value,
    },
  ];

  const pastBusinessExpenseData: DivergenceInExecutionItemBefore2025[] = [
    {
      fiscalYear: project?.fiscalYear ? project.fiscalYear - 2 : undefined,
      estimatedBusinessExpense: twoYearsAgoDivergence?.estimatedBusinessExpense,
      businessExpense: twoYearsAgoDivergence?.businessExpense,
      divergenceAmount: twoYearsAgoDivergence?.divergenceAmount,
      reason: executionDivergences?.[1]?.reason,
      divergenceRate: twoYearsAgoDivergence?.divergenceRate,
    },
    {
      fiscalYear: project?.fiscalYear ? project.fiscalYear - 1 : undefined,
      estimatedBusinessExpense: oneYearAgoDivergence?.estimatedBusinessExpense,
      businessExpense: oneYearAgoDivergence?.businessExpense,
      divergenceAmount: oneYearAgoDivergence?.divergenceAmount,
      reason: executionDivergences?.[0]?.reason,
      divergenceRate: oneYearAgoDivergence?.divergenceRate,
    },
  ];

  return (
    <ReportSection
      title={t('income-and-expenditure-title')}
      items={[
        {
          subtitle: `${t('income-and-expenditure-title')}\n${t('report-amount-unit-description')}`,
          item: (
            <Flex vertical gap={8}>
              {IncomeAndExpenditureItems.length > 0 ? (
                <KsFundIncomeAndExpenditureItem
                  items={IncomeAndExpenditureItems}
                  fiscalYear={project?.fiscalYear}
                />
              ) : (
                <KsFundIncomeAndExpenditureItem
                  items={emptyIncomeAndExpenditureItem}
                  fiscalYear={project?.fiscalYear}
                />
              )}
            </Flex>
          ),
        },
        {
          subtitle: `${t('situation-of-divergence-in-execution')}\n${t('report-amount-unit-description')}`,
          item: (
            <KsFundDivergenceInExecutionItemBefore2025
              data={pastBusinessExpenseData}
            />
          ),
        },
      ]}
    />
  );
};
